/**
 * NotificationsHistorySection — histórico paginado das notificações do user
 * logado (FEAT-V2-028). Renderizado em `/perfil/notificacoes` abaixo da matriz
 * de preferências.
 *
 * Filtros: type (dropdown), lida (chips Todas/Lidas/Não-lidas), range data
 * (from/to). Paginação clicável (Anterior/Próxima) usando envelope
 * `{items, total, page, hasMore}` do endpoint paginado.
 *
 * Sem virtualização — limit 50 por página é leve. Se >5000 notifs aparecer
 * como caso real, considerar react-window (TD-V2-NOTIF-HISTORY-VIRTUAL).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Card, Select, SelectOption, Input, Label, EmptyState } from '@/components/ui';
import { formatRelativeDate, formatDataHoraSegundos } from '@/lib/format-date';
import { EVENT_TYPES } from '@/lib/notification-preferences';
import {
  useNotificationsHistory,
  type NotificationsHistoryFilters,
} from '@/lib/notifications-store';

type LidaFilter = 'all' | 'read' | 'unread';

export function NotificationsHistorySection({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [lidaFilter, setLidaFilter] = useState<LidaFilter>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filters = useMemo<NotificationsHistoryFilters>(
    () => ({
      type: typeFilter || null,
      lida: lidaFilter === 'all' ? null : lidaFilter === 'read',
      from: from || null,
      to: to || null,
    }),
    [typeFilter, lidaFilter, from, to],
  );

  // Resetar pra page 1 quando muda filtro.
  function applyFilterChange(setter: () => void) {
    return () => {
      setter();
      setPage(1);
    };
  }

  const { data, isLoading } = useNotificationsHistory(userId, page, filters);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasMore = data?.hasMore ?? false;

  function handleNavigate(linkUrl: unknown) {
    if (typeof linkUrl === 'string' && linkUrl.length > 0) navigate(linkUrl);
  }

  return (
    <Card data-testid="notifications-history" className="mt-6">
      <header className="px-4 py-3 border-b border-line dark:border-line-dark">
        <h2 className="font-display text-xl">Histórico de notificações</h2>
        <p className="text-xs text-ink-3 dark:text-ink-dark-3">
          {total} notificação{total === 1 ? '' : 's'} no total.
        </p>
      </header>

      {/* Filtros */}
      <div className="px-4 py-3 border-b border-line dark:border-line-dark flex flex-wrap gap-3 items-end">
        <div>
          <Label htmlFor="filter-lida">Status</Label>
          <div className="flex gap-1" id="filter-lida">
            {(['all', 'unread', 'read'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={applyFilterChange(() => setLidaFilter(v))}
                data-testid={`hist-filter-lida-${v}`}
                className={`text-xs px-2.5 py-1 rounded-pill transition-colors ${
                  lidaFilter === v
                    ? 'bg-red text-white'
                    : 'bg-paper-warm dark:bg-paper-dark-warm text-ink-2 dark:text-ink-dark-2'
                }`}
              >
                {v === 'all' ? 'Todas' : v === 'unread' ? 'Não-lidas' : 'Lidas'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="filter-type">Tipo</Label>
          <Select
            id="filter-type"
            data-testid="hist-filter-type"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="text-xs"
          >
            <SelectOption value="">Todos</SelectOption>
            {EVENT_TYPES.map((evt) => (
              <SelectOption key={evt.slug} value={evt.slug}>
                {evt.label}
              </SelectOption>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="filter-from">De</Label>
          <Input
            id="filter-from"
            data-testid="hist-filter-from"
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            className="text-xs w-40"
          />
        </div>
        <div>
          <Label htmlFor="filter-to">Até</Label>
          <Input
            id="filter-to"
            data-testid="hist-filter-to"
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            className="text-xs w-40"
          />
        </div>
      </div>

      {/* Lista */}
      <div className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <p className="text-xs text-ink-3 italic px-4 py-6">Carregando…</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="Sem notificações neste filtro"
            description="Ajuste os filtros ou aguarde novas notificações."
          />
        ) : (
          <ul className="divide-y divide-line dark:divide-line-dark">
            {items.map((n) => {
              const title =
                typeof n.payload.title === 'string'
                  ? n.payload.title
                  : EVENT_TYPES.find((e) => e.slug === n.type)?.label ?? n.type;
              const body =
                typeof n.payload.body === 'string' ? n.payload.body : null;
              const linkUrl = n.payload.linkUrl;
              return (
                <li
                  key={n.id}
                  data-testid={`hist-row-${n.id}`}
                  className={`px-4 py-3 text-sm ${
                    !n.lida ? 'bg-red-soft/20 dark:bg-red-soft-dark/20' : ''
                  } ${linkUrl ? 'hover:bg-paper-warm dark:hover:bg-paper-dark-warm cursor-pointer' : ''}`}
                  onClick={() => handleNavigate(linkUrl)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-ink-1 dark:text-ink-dark-1 truncate">
                        {title}
                      </p>
                      {body && (
                        <p className="text-xs text-ink-2 dark:text-ink-dark-2 mt-0.5 line-clamp-2">
                          {body}
                        </p>
                      )}
                    </div>
                    <span
                      className="text-xs text-ink-3 flex-shrink-0"
                      title={formatDataHoraSegundos(n.criadoEmIso)}
                    >
                      {formatRelativeDate(n.criadoEmIso)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Paginação */}
      {(page > 1 || hasMore) && (
        <footer className="px-4 py-3 border-t border-line dark:border-line-dark flex items-center justify-between text-xs">
          <span className="text-ink-3 tabular-nums" data-testid="hist-page-info">
            Página {page}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="hist-prev"
            >
              Anterior
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
              data-testid="hist-next"
            >
              Próxima
            </Button>
          </div>
        </footer>
      )}
    </Card>
  );
}
